import * as React from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from "chart.js";
import id from "date-fns/locale/id";
import { Line } from "react-chartjs-2";
import {
    ParamsGetLaporanTotal,
    ParamsGrafikPenjualan,
} from "../../constants/types";

import { Refresh } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../context/authContext";
import useGrafikPenjualan from "../../services/queries/useGrafikPenjualan";
import useLaporanTotal from "../../services/queries/useLaporanTotal";
import toRibuan from "../../utils/toRibuan";
import toRupiah from "../../utils/toRupiah";

// import "../components/Datepicker/Datepicker.module.css";

registerLocale("id", id);
setDefaultLocale("id");

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
);

type DateFilter = [Date | null, Date | null];

const LaporanCoWork = () => {
    const moment = require("moment");
    let now = moment().format("YYYY/MM/DD");
    const today = new Date();
    const theme = useTheme();

    const isPhoneScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { ukmIdUser } = useAuth();

    const [dateRange, setDateRange] = React.useState<DateFilter>([
        new Date(),
        new Date(),
    ]);
    const [startDate, endDate] = dateRange;

    const initialParamsGrafikPenjualan = {
        ukmId: ukmIdUser && ukmIdUser[0],
        tanggalAwal: now,
        tanggalAkhir: now,
        coworkOnly: 1,
    };

    const [paramsGrafikPenjualan, setParamsGrafikPenjualan] =
        React.useState<ParamsGrafikPenjualan>(initialParamsGrafikPenjualan);

    const { data: grafikPenjualan, refetch: refetchGrafikPenjualan } =
        useGrafikPenjualan(paramsGrafikPenjualan);

    const initialParamsLaporanTotal = {
        ukmId: ukmIdUser && ukmIdUser[0],
        tanggalAwal: now,
        tanggalAkhir: now,
        coworkOnly: 1,
    };
    const [paramsLaporanTotal, setParamsLaporanTotal] =
        React.useState<ParamsGetLaporanTotal>(initialParamsLaporanTotal);
    const {
        data: laporanTotal,
        status: loadingLaporanTotal,
        refetch: refetchLaporanTotal,
        isError: isErrorLaporanTotal,
    } = useLaporanTotal(paramsLaporanTotal);

    const handleChangePeriode = (event: SelectChangeEvent) => {
        setDateRange([new Date(event.target.value), new Date()]);
        setParamsGrafikPenjualan((prev) => ({
            ...prev,
            tanggalAwal: String(event.target.value),
            tanggalAkhir: now,
            page: 1,
        }));
        setParamsLaporanTotal((prev) => ({
            ...prev,
            tanggalAwal: String(event.target.value),
            tanggalAkhir: now,
            page: 1,
        }));
        refetchLaporanTotal();
        refetchGrafikPenjualan();
    };

    const handleChangeJangkaWaktu = (
        tanggalAwal: string,
        tanggalAkhir: string,
    ) => {
        setParamsGrafikPenjualan((prev) => ({
            ...prev,
            tanggalAwal: tanggalAwal,
            tanggalAkhir: tanggalAkhir,
            page: 1,
        }));
        setParamsLaporanTotal((prev) => ({
            ...prev,
            tanggalAwal: tanggalAwal,
            tanggalAkhir: tanggalAkhir,
            page: 1,
        }));
        refetchLaporanTotal();
        refetchGrafikPenjualan();
    };

    function subtractYears(numOfYears: number, date = new Date()) {
        date.setFullYear(date.getFullYear() - numOfYears);

        return date;
    }

    const chartOptions = {
        // maintainAspectRatio: false,
        responsive: true,
        interaction: {
            mode: "index" as const,
            intersect: false,
        },
        plugins: {
            legend: {
                position: "bottom" as const,
            },
        },
        stacked: false,
        locale: "id-ID",
        scales: {
            x: {
                display: true,
                grid: {
                    drawOnChartArea: false,
                },
            },
            y: {
                display: true,
                position: "left" as const,
                ticks: {
                    callback: function (value: any, index: any, values: any) {
                        return handleConvertSatuan(value);
                    },
                },
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Total Pendapatan",
                },
            },
            y1: {
                display: true,
                position: "right" as const,
                grid: {
                    drawOnChartArea: false,
                },
                gridLines: {
                    drawBorder: false,
                },
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Jumlah Transaksi",
                },
            },
        },
    };

    const data = {
        labels: grafikPenjualan?.labels ?? [],
        datasets: [
            {
                label: "Pendapatan",
                data: grafikPenjualan?.data.totalPendapatan ?? [],
                borderColor: "#45A779",
                backgroundColor: "#45A779",
                yAxisID: "y",
                tension: 0.5,
                pointRadius: 0,
            },
            {
                label: "Jumlah Transaksi",
                data: grafikPenjualan?.data.totalTransaksi ?? [],
                borderColor: "#e8d43a",
                backgroundColor: "#e8d43a",
                yAxisID: "y1",
                tension: 0.5,
                pointRadius: 0,
            },
        ],
    };

    const handleConvertSatuan = (value: string) => {
        if (Number(value) >= 1000000000)
            return Number(value) / 1000000000 + "m";
        if (Number(value) >= 1000000) return Number(value) / 1000000 + "jt";
        if (Number(value) >= 1000) return Number(value) / 1000 + "rb";
        return String(value);
    };

    // const isError = isErrorGrafik || isErrorLaporanTotal;
    // if (isError) {
    //     return (
    //         <PageLayout>
    //             <ErrorFetchView />
    //         </PageLayout>
    //     );
    // }

    return (
        <Stack spacing={3} paddingY={3}>
            <Grid container alignItems="center" spacing={3}>
                <Grid item xs={12} md={7} lg={9}>
                    <Typography variant="h6" fontWeight="bold">
                        Laporan Co Work
                    </Typography>
                </Grid>
            </Grid>
            <Grid container gap={2} alignItems="center">
                <Grid item xs={12} md={4}>
                    <Select
                        value={
                            paramsLaporanTotal.tanggalAwal === now ||
                            paramsLaporanTotal.tanggalAwal ===
                                moment()
                                    .subtract(7, "days")
                                    .format("YYYY-MM-DD") ||
                            paramsLaporanTotal.tanggalAwal ===
                                moment()
                                    .subtract(1, "month")
                                    .format("YYYY-MM-DD") ||
                            paramsLaporanTotal.tanggalAwal ===
                                moment()
                                    .subtract(3, "months")
                                    .format("YYYY-MM-DD") ||
                            paramsLaporanTotal.tanggalAwal ===
                                moment()
                                    .subtract(1, "years")
                                    .format("YYYY-MM-DD")
                                ? paramsLaporanTotal.tanggalAwal
                                : "Custom"
                        }
                        size="small"
                        fullWidth
                        onChange={handleChangePeriode}
                        // sx={{ width: 220 }}
                    >
                        <MenuItem value={now}>Hari ini</MenuItem>
                        <MenuItem
                            value={moment()
                                .subtract(7, "days")
                                .format("YYYY-MM-DD")}
                        >
                            1 Minggu Terakhir
                        </MenuItem>
                        <MenuItem
                            value={moment()
                                .subtract(1, "month")
                                .format("YYYY-MM-DD")}
                        >
                            1 Bulan Terakhir
                        </MenuItem>
                        <MenuItem
                            value={moment()
                                .subtract(3, "months")
                                .format("YYYY-MM-DD")}
                        >
                            3 Bulan Terakhir
                        </MenuItem>
                        <MenuItem
                            value={moment()
                                .subtract(1, "years")
                                .format("YYYY-MM-DD")}
                        >
                            1 Tahun Terakhir
                        </MenuItem>
                        <MenuItem value={"Custom"} sx={{ display: "none" }}>
                            Custom
                        </MenuItem>
                    </Select>
                </Grid>
                <Grid item xs={12} md={4}>
                    <DatePicker
                        className="range-filter"
                        dateFormat="dd/MM/yyyy"
                        disabledKeyboardNavigation
                        // open={isOpenKalender}
                        onFocus={(e) => e.target.blur()}
                        selectsRange={true}
                        startDate={startDate}
                        endDate={endDate}
                        onChange={(update) => {
                            setDateRange(update);
                            if (update[1]) {
                                handleChangeJangkaWaktu(
                                    moment(update[0]).format("YYYY/MM/DD"),
                                    moment(update[1]).format("YYYY/MM/DD"),
                                );
                            }
                        }}
                        maxDate={today}
                        minDate={subtractYears(1)}
                        // showPreviousMonths={
                        //     isPhoneScreen || isTabletScreen ? false : true
                        // }
                        monthsShown={isPhoneScreen ? 1 : 2}
                        // inline
                        customInput={
                            <Stack
                                display="flex"
                                justifyContent="space-between"
                                direction="row"
                                width="100%"
                                bgcolor="#FFFF"
                                height="40px"
                                sx={{
                                    border: "1px solid #cbcbcb",
                                    borderRadius: "4px",
                                    alignItems: "center",
                                    padding: 2,
                                    cursor: "pointer",
                                }}
                            >
                                <Typography>
                                    {moment(
                                        paramsLaporanTotal.tanggalAwal,
                                    ).format("DD/MM/YYYY")}{" "}
                                    -{" "}
                                    {paramsLaporanTotal.tanggalAkhir &&
                                        moment(
                                            paramsLaporanTotal.tanggalAkhir,
                                        ).format("DD/MM/YYYY")}
                                </Typography>
                                <ArrowDropDownIcon
                                    sx={{ marginRight: "-9px" }}
                                />
                            </Stack>
                        }
                    />
                </Grid>
            </Grid>
            <Box
                display="flex"
                flexDirection={isPhoneScreen ? "column" : "row"}
                sx={{
                    width: "100%",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: "6px",
                }}
            >
                <Stack spacing={2} padding={3} width="100%">
                    <Typography
                        variant="body2"
                        color="#6D6D6D"
                        fontWeight="bold"
                    >
                        Pendapatan
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                        {loadingLaporanTotal === "loading" ? (
                            <Skeleton variant="text" />
                        ) : isErrorLaporanTotal ? (
                            <Stack direction="row" alignItems="center" gap={2}>
                                <Typography color="GrayText">
                                    Muat ulang
                                </Typography>
                                <IconButton
                                    onClick={() => refetchLaporanTotal()}
                                >
                                    <Refresh fontSize="small" />
                                </IconButton>
                            </Stack>
                        ) : (
                            toRupiah(Number(laporanTotal?.totalPendapatan))
                        )}
                    </Typography>
                </Stack>
                <Divider
                    orientation={isPhoneScreen ? "horizontal" : "vertical"}
                    flexItem
                />
                <Stack spacing={2} padding={3} width="100%">
                    <Typography
                        variant="body2"
                        color="#6D6D6D"
                        fontWeight="bold"
                    >
                        Total Transaksi
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                        {loadingLaporanTotal === "loading" ? (
                            <Skeleton variant="text" />
                        ) : isErrorLaporanTotal ? (
                            <Stack direction="row" alignItems="center" gap={2}>
                                <Typography color="GrayText">
                                    Muat ulang
                                </Typography>
                                <IconButton
                                    onClick={() => refetchLaporanTotal()}
                                >
                                    <Refresh fontSize="small" />
                                </IconButton>
                            </Stack>
                        ) : (
                            `${toRibuan(
                                Number(laporanTotal?.totalTransaksi),
                            )} Transaksi`
                        )}
                    </Typography>
                </Stack>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: "6px",
                    padding: 3,
                }}
            >
                <Line options={chartOptions} data={data} />
            </Box>
        </Stack>
    );
};

export default LaporanCoWork;
